import * as React from 'react';
import { z } from 'zod';
import { zodResolver } from '@hookform/resolvers/zod';
import { useState } from 'react';
import Grid from '@mui/material/Grid';
import { Typography, Button, TextField, IconButton, Box, InputAdornment, InputLabel } from '@mui/material';
import { useForm, Controller } from 'react-hook-form';
import { AuthService } from '../../../service/auth.service';
import { useNavigate } from 'react-router-dom';
import { Visibility, VisibilityOff } from '@mui/icons-material';
import { encryptData } from '../../../helper/utils';
import Loader from '../../components/loader';
import {
  authShellSx,
  authMarketingPanelSx,
  authMarketingInnerSx,
  authBrandSx,
  authHeadlineSx,
  authSubheadlineSx,
  authCapabilitiesWrapSx,
  authCapabilityPillSx,
  authFormPanelSx,
  authCardSx,
  authCardHeaderSx,
  authLogoSx,
  authTitleSx,
  authSubtitleSx,
  authLabelSx,
  authFieldSx,
  authPrimaryButtonSx,
  authForgotPasswordSx,
  requiredAsteriskSx,
} from '../../styles/authStyles';
import NotificationSnackbar from '../../components/notificationSnackbar';

export default function Login() {
  const navigate = useNavigate();
  const authService = new AuthService();
  const [showPassword, setShowPassword] = useState(false);
  const [loading, setLoading] = useState(false);
  const [openAlert, setOpenAlert] = useState(false);
  const [alert, setAlert] = useState('');
  const [responseMsg, setResponseMsg] = useState('');

  const ALERT_DURATION = 2000;
  const alertClose = () => {
    setAlert('');
    setOpenAlert(false);
  };


  const loginSchema = z.object({
    email: z
      .string({ required_error: 'Email Id is required' })
      .min(1, { message: 'Email Id is required' })
      .email({ message: 'Invalid email format' }),
    password: z.string().min(1, { message: 'Password is required' }),
  });

  const { control, handleSubmit } = useForm({ resolver: zodResolver(loginSchema) });

  const onSubmit = async (data) => {
    const encryptedEmail = encryptData(data.email.toLowerCase());
    const encryptedPassword = encryptData(data.password);
    const authData = { email: encryptedEmail, password: encryptedPassword };

    setLoading(true);

    try {
      const res = await authService.login(authData);

      if (res.user) {
        setResponseMsg("Login successful");
        setAlert('true');
        setOpenAlert(true);
        setTimeout(() => {
          alertClose();
          navigate('/user');
        }, ALERT_DURATION);
      } else {
        setResponseMsg("Login failed. Please try again.");
        setAlert('false');
        setOpenAlert(true);
        setTimeout(() => {
          alertClose();
        }, ALERT_DURATION);
      }
    } catch (err) {
      setResponseMsg('Login failed. Please try again.');
      setAlert('false');
      setOpenAlert(true);
      setTimeout(() => {
        alertClose();
      }, ALERT_DURATION);
    } finally {
      setLoading(false);
    }
  };

  return (
    <>
      <Grid container sx={authShellSx}>
        <Grid
          size={{ sm: 12, xs: 12, md: 5, lg: 5, xl: 5 }}
          sx={{
            order: { xs: 2, md: 1 },
            ...authMarketingPanelSx,
          }}
        >
          <Box sx={authMarketingInnerSx}>
            <Box sx={{ pb: { xs: 3.5, sm: 4.5, md: 5 } }}>
              <Typography sx={authBrandSx}>eKutir</Typography>
              <Typography sx={authHeadlineSx}>Global AgTech Platform</Typography>
              <Typography sx={authSubheadlineSx}>Powering Agricultural Innovation</Typography>
            </Box>

            <Box sx={authCapabilitiesWrapSx}>
              {['ALLIN FIX', 'ALLIN FIT', 'ALLIN FLEX', 'ALLIN IMPACT', 'ALLIN TRUST'].map((item) => (
                <Typography key={item} sx={authCapabilityPillSx}>
                  {item}
                </Typography>
              ))}
            </Box>
          </Box>
        </Grid>

        <Grid
          size={{ sm: 12, xs: 12, md: 7, lg: 7, xl: 7 }}
          sx={{
            order: { xs: 1, md: 2 },
            ...authFormPanelSx,
          }}
        >
          <Box sx={authCardSx}>
            <Box sx={authCardHeaderSx}>
              <Box
                component="img"
                src="/assets/ekutire.png"
                alt="eKutir"
                sx={authLogoSx}
              />
              <Typography sx={authTitleSx}>Welcome to eKutir</Typography>
              <Typography sx={authSubtitleSx}>Sign in to access your dashboard</Typography>
            </Box>

            <Box component="form" onSubmit={handleSubmit(onSubmit)} sx={{ width: '100%' }}>
              <Grid container rowSpacing={{ xs: 2.5, sm: 3 }}>
              <Grid size={{ sm: 12, xs: 12, md: 12, lg: 12, xl: 12 }}>
                <InputLabel shrink htmlFor="email" sx={authLabelSx}>
                  Email Address <Box component="span" sx={requiredAsteriskSx}>*</Box>
                </InputLabel>
                <Controller name="email" control={control} defaultValue=""
                  render={({ field, fieldState: { error } }) => (
                    <TextField
                      {...field}
                      fullWidth
                      id="email"
                      placeholder="your@email.com"
                      type="text"
                      variant="outlined"
                      error={!!error}
                      helperText={error ? error.message : ''}
                      sx={authFieldSx}
                    />
                  )}
                />
              </Grid>

              <Grid size={12}>
                <InputLabel shrink htmlFor="password" sx={authLabelSx}>
                  Password <Box component="span" sx={requiredAsteriskSx}>*</Box>
                </InputLabel>
                <Controller name="password" control={control} defaultValue=""
                  render={({ field, fieldState: { error } }) => (
                    <TextField {...field} fullWidth id="password" placeholder="Enter password" type={showPassword ? 'text' : 'password'} variant="outlined"
                      InputProps={{
                        endAdornment: (
                          <InputAdornment position="end">
                            <IconButton onClick={() => setShowPassword(!showPassword)} edge="end" sx={{ color: '#6C6A86' }}>
                              {showPassword ? <Visibility /> : <VisibilityOff />}
                            </IconButton>
                          </InputAdornment>
                        )
                      }} error={!!error} helperText={error ? error.message : ''} sx={authFieldSx} />
                  )}
                />
              </Grid>

              <Grid size={{ sm: 12, xs: 12, md: 12, lg: 12, xl: 12 }}>
                <Button variant="contained" type="submit" fullWidth sx={authPrimaryButtonSx}> Sign In</Button>
              </Grid>
            </Grid>

            {loading && <Loader />}
          </Box>


          <Box sx={{ pt: { xs: 3, sm: 4 }, width: '100%' }}>
            <Typography
              onClick={() => { setLoading(true); navigate('/auth/forgotPassword') }}
              sx={authForgotPasswordSx}
            >
              Forgot password ?
            </Typography>
          </Box>
          </Box>
        </Grid>
      </Grid>

      <NotificationSnackbar
        open={openAlert && !!alert}
        onClose={alertClose}
        message={responseMsg}
        severity={alert === 'true' ? 'success' : 'error'}
        messageTestId={alert === 'true' ? 'alert-success' : 'alert-error'}
      />
    </>
  );
}
